import { BLUE_200, BLUE_900, GRAY_300, GRAY_600, GRAY_700, GRAY_900, GREEN_600 } from 'constants/styling/theme'
import { ConversationSummaryDTO } from 'models/conversations'

import { ConversationStatusBadge } from '../../ConversationStatusBadge/ConversationStatusBadge'
import { FC } from 'react'
import { MUIBadge } from 'components/common/MUIBadge'
import Stack from '@mui/material/Stack'
import { TypingDots } from '../../TypingDots/TypingDots'
import Typography from '@mui/material/Typography'
import { UnreadCountBadge } from '../../UnreadCountBadge/UnreadCountBadge'
import { distinctLabels, getConversationDisplayTitle } from '../../helpers/conversationDisplay'
import { getRelativeTimePart } from '../../helpers/relativeTime'
import { lastMessagePreviewText } from '../../helpers/chatAttachments'
import { useChatWidget } from '../../ChatWidget.context'
import { useTranslation } from 'react-i18next'

interface Props {
  conversation: ConversationSummaryDTO
  currentUserId?: string
  isSelected: boolean
  onClick: () => void
}

/** Single conversation row in the list (title, last-message preview, timestamp, unread count). */
export const ConversationItem: FC<Props> = ({ conversation, currentUserId, isSelected, onClick }) => {
  const { t, i18n } = useTranslation(['chat'])

  const { typingConversationIds } = useChatWidget()
  // Don't surface "typing…" in the list for the conversation that's currently open beside it (the
  // drawer's two-pane layout) — the live typing indicator already shows inside the open thread.
  const isTyping = !isSelected && typingConversationIds.has(conversation.id)

  const title = getConversationDisplayTitle(conversation, currentUserId, t)
  const preview = lastMessagePreviewText(conversation, t)
  const hasUnread = (conversation.unreadCount ?? 0) > 0

  const renderTimestamp = () => {
    const at = conversation.lastMessageAt ?? conversation.updatedAt
    if (!at) return null

    const part = getRelativeTimePart(new Date(at))
    switch (part.kind) {
      case 'just_now': return t('chat:time.just_now')
      case 'minutes': return t('chat:time.minutes_ago', { count: part.value })
      case 'hours': return t('chat:time.hours_ago', { count: part.value })
      case 'yesterday': return t('chat:time.yesterday')
      // Locale-aware month/day order (en → 06/12, de → 12.06.) via the active i18next language.
      case 'date': return new Intl.DateTimeFormat(i18n.language, { month: 'numeric', day: 'numeric' }).format(part.date)
    }
  }

  return (
    <Stack
      direction="row"
      gap="1.2rem"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        padding: '1.2rem',
        borderRadius: '0.8rem',
        borderLeft: `0.3rem solid ${isSelected ? BLUE_900 : 'transparent'}`,
        backgroundColor: isSelected ? BLUE_200 : 'transparent',
        '&:hover': { backgroundColor: isSelected ? BLUE_200 : GRAY_300 },
      }}
    >

      <Stack flex={1} minWidth={0} gap="0.2rem">

        <Stack direction="row" justifyContent="space-between" alignItems="center" gap="0.8rem">

          <Typography
            variant="text-sm"
            fontWeight={hasUnread ? 700 : 600}
            color={GRAY_900}
            noWrap
            sx={{ minWidth: 0 }}
          >
            {title}
          </Typography>

          <Stack direction="row" alignItems="center" gap="0.6rem" flexShrink={0}>

            <Typography variant="text-xs" color={GRAY_600} noWrap>
              {renderTimestamp()}
            </Typography>

            {hasUnread &&
              <UnreadCountBadge count={conversation.unreadCount ?? 0} />
            }

          </Stack>

        </Stack>

        {isTyping
          ? <Stack direction="row" alignItems="center" gap="0.6rem">

            <TypingDots color={GREEN_600} size="0.5rem" />

            <Typography variant="text-sm" sx={{ color: GREEN_600, fontStyle: 'italic' }}>
              {t('chat:thread.typing')}
            </Typography>

          </Stack>
          : !!preview &&
            <Typography
              variant="text-sm"
              color={GRAY_700}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {preview}
            </Typography>
        }

        <Stack direction="row" gap="0.6rem" flexWrap="wrap" alignItems="center" sx={{ marginTop: '0.4rem' }}>

          <ConversationStatusBadge conversation={conversation} />

          {distinctLabels((conversation.contexts ?? []).map((context) => context.label ?? context.id)).map((label) => (
            <MUIBadge
              key={label}
              size="xs"
              color="white"
              label={label}
              sx={{ maxWidth: '18rem' }}
            />
          ))}

        </Stack>

      </Stack>

    </Stack>
  )
}
